import React, { useState, useEffect, useRef } from "react";
import runningVersion from "../version.json";

export default function AppVersionChecker() {
  const [showModal, setShowModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastCheckedRef = useRef(0);
  const newVersionPendingRef = useRef(false);
  const latestVersionRef = useRef(null);

  const checkHasUnsavedChanges = () => {
    const pathname = window.location.pathname;
    const isFormPage =
      pathname.includes("/onboarding") ||
      pathname.includes("/add-vehicle") ||
      pathname.includes("/set-display-name") ||
      pathname.includes("/set-password");

    const inputs = document.querySelectorAll(
      'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]), textarea, select'
    );
    let hasTypedData = false;
    inputs.forEach((input) => {
      if (input.value && input.value.trim().length > 0) {
        hasTypedData = true;
      }
    });

    return isFormPage && hasTypedData;
  };

  const isActivelyInteracting = () => {
    const active = document.activeElement;
    if (active && ["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName)) {
      return true;
    }
    return false;
  };

  const triggerModalIfSafe = () => {
    if (!newVersionPendingRef.current) return;
    if (isActivelyInteracting()) {
      return;
    }
    const unsaved = checkHasUnsavedChanges();
    setHasUnsavedChanges(unsaved);
    setShowModal(true);
  };

  const checkForUpdates = async () => {
    try {
      const now = Date.now();
      if (now - lastCheckedRef.current < 20 * 1000) return;
      lastCheckedRef.current = now;

      const res = await fetch(`/version.json?_t=${now}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      if (!res.ok) return;
      const latestVersion = await res.json();
      latestVersionRef.current = latestVersion;

      if (process.env.NODE_ENV !== "production") {
        console.log("Current Version:", runningVersion);
        console.log("Latest Version:", latestVersion);
        console.log("Version Check Success");
      }

      const dismissed = localStorage.getItem("sramika_dismissed_version");

      const isNewBuildNumber =
        latestVersion?.buildNumber &&
        runningVersion?.buildNumber &&
        latestVersion.buildNumber !== runningVersion.buildNumber;

      // Already reloaded for this version in the last 10 minutes and still on the old one:
      // the new pages are on their way - wait quietly instead of asking again (30 Oct 2026).
      let reloadedFor = null;
      try { reloadedFor = JSON.parse(sessionStorage.getItem("sramika_reloaded_for") || "null"); } catch { /* ignore */ }
      const justTried = reloadedFor && reloadedFor.build === latestVersion?.buildNumber && Date.now() - reloadedFor.at < 10 * 60 * 1000;

      if (isNewBuildNumber && latestVersion.buildNumber !== dismissed && !justTried) {
        if (process.env.NODE_ENV !== "production") {
          console.log("Version Updated");
        }
        newVersionPendingRef.current = true;
        triggerModalIfSafe();
      }
    } catch (err) {
      // Silently catch network errors during polling
    }
  };

  useEffect(() => {
    checkForUpdates();

    const interval = setInterval(() => {
      checkForUpdates();
    }, 10 * 60 * 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdates();
      }
    };

    const handleFocusBlur = () => {
      if (newVersionPendingRef.current && !showModal) {
        setTimeout(triggerModalIfSafe, 500);
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focusin", handleFocusBlur);
    window.addEventListener("focusout", handleFocusBlur);

    return () => {
      clearInterval(interval);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focusin", handleFocusBlur);
      window.removeEventListener("focusout", handleFocusBlur);
    };
  }, [showModal]);

  const handleRefreshNow = async () => {
    setIsRefreshing(true);
    localStorage.removeItem("sramika_dismissed_version");
    try {
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
    } catch (e) {}

    const url = new URL(window.location.href);
    try { sessionStorage.setItem("sramika_reloaded_for", JSON.stringify({ build: latestVersionRef.current?.buildNumber || null, at: Date.now() })); } catch { /* ignore */ }
    url.searchParams.set("_v", Date.now());
    window.location.replace(url.toString());
  };

  const handleLater = () => {
    setShowModal(false);
    newVersionPendingRef.current = false;
    if (latestVersionRef.current?.buildNumber) {
      localStorage.setItem("sramika_dismissed_version", latestVersionRef.current.buildNumber);
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full border border-gray-100 text-center relative overflow-hidden">
        <div className="w-12 h-12 rounded-full bg-[#F6FAEC] text-[#7BAE00] flex items-center justify-center mx-auto mb-3 shadow-inner">
          <svg
            className={`w-6 h-6 ${isRefreshing ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>

        <h3 className="text-lg font-bold text-gray-900 mb-1">
          New Version Available
        </h3>
        <p className="text-xs text-gray-600 leading-relaxed px-2">
          A newer version of Sramika is available with improvements and fixes.
        </p>

        {hasUnsavedChanges && (
          <div className="mt-3.5 p-3 bg-amber-50 border border-amber-200 rounded-xl text-left flex items-start gap-2.5">
            <span className="text-amber-600 font-bold text-sm mt-0.5">⚠️</span>
            <div className="text-xs text-amber-900 font-medium leading-normal">
              <p className="font-bold">You have unsaved changes.</p>
              <p className="text-[11px] text-amber-800 mt-0.5">
                Refreshing now may lose them.
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mt-5">
          <button
            type="button"
            onClick={handleLater}
            disabled={isRefreshing}
            className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition-all disabled:opacity-50"
          >
            Later
          </button>
          <button
            type="button"
            onClick={handleRefreshNow}
            disabled={isRefreshing}
            className="flex-1 py-2.5 px-4 bg-[#7BAE00] hover:bg-[#689300] text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            {isRefreshing ? "Updating..." : "Refresh Now"}
          </button>
        </div>
      </div>
    </div>
  );
}
